import { useState, useRef } from 'react'
import getErrorMessageForRequest from '../getErrorMessageForRequest'
import { getRecaptchaToken } from '../../components/forms/recaptchaUtil'

const RECAPTCHA_FAILED_MESSAGE = 'Sending a code requires reCAPTCHA but it failed to load. ' +
  'Ensure google.com/recaptcha/api.js is not blocked by your browser or network.'

// The email-verification code round trip, shared by every screen that runs
// one — the prompt flow modal, and the account page's update-email /
// add-backup modals:
//   requestCode -> (403 reauth_required -> reauth modal -> resubmit) -> code sent
//   submitCode / resend
// The calling screen still owns which step it shows: onCodeSent(purpose)
// fires whenever a code goes out (including after a reauth resubmit), so it
// can move on to its code-entry step. Pass the connected action creators in.
export default function useEmailVerificationRequest({
  userId, createEmailVerification, resendEmailVerification, verifyEmailVerification, onCodeSent,
}) {
  const [verification, setVerification] = useState(null) // { id, purpose, email, resendAvailableAt }
  const [requestLoading, setRequestLoading] = useState(false)
  const [requestError, setRequestError] = useState('')
  // { purpose, email } of the request that drew a 403 reauth_required, so the
  // reauth modal's result can resubmit it — kept separate from showReauth
  // since ReauthenticationModal hides itself before calling onAuthenticate
  const [pendingReauth, setPendingReauth] = useState(null)
  const [showReauth, setShowReauth] = useState(false)
  // always the latest onCodeSent — it's called after an await, by which
  // point the closure from when the request started may be stale
  const onCodeSentRef = useRef(onCodeSent)
  onCodeSentRef.current = onCodeSent

  // email is omitted for verifyPrimary (the code goes to the current address)
  const requestCode = async ({ purpose, email }, authentication) => {
    setRequestLoading(true)
    setRequestError('')
    // a fresh token per request — the reauth resubmit needs its own
    let recaptchaResponse
    try {
      recaptchaResponse = await getRecaptchaToken()
    } catch (err) {
      setRequestLoading(false)
      setRequestError(RECAPTCHA_FAILED_MESSAGE)
      return
    }
    let response
    try {
      response = await createEmailVerification(userId, {
        purpose, email, recaptchaResponse, ...authentication,
      })
    } catch (err) {
      setRequestLoading(false)
      if (err.response && err.response.status === 403) {
        setPendingReauth({ purpose, email })
        setShowReauth(true)
        return
      }
      setRequestError(getErrorMessageForRequest({
        requestName: 'createEmailVerification',
        response: err.response,
        data: { email, authType: authentication && authentication.type },
      }))
      return
    }
    setRequestLoading(false)
    setPendingReauth(null)
    setVerification({
      id: response.id, purpose, email, resendAvailableAt: response.resendAvailableAt,
    })
    if (onCodeSentRef.current) onCodeSentRef.current(purpose)
  }

  const handleReauthenticate = (authentication) => {
    if (!pendingReauth) { return }
    requestCode(pendingReauth, authentication)
  }

  // resolves with the verify response, or rejects with an Error whose message
  // is ready to show — the shape EmailVerificationCodeEntry's onSubmitCode expects
  const submitCode = async (code) => {
    try {
      return await verifyEmailVerification(userId, verification.id, code)
    } catch (err) {
      throw new Error(getErrorMessageForRequest({ requestName: 'verifyEmailVerificationCode', response: err.response }))
    }
  }

  const resend = async () => {
    try {
      return await resendEmailVerification(userId, verification.id)
    } catch (err) {
      throw new Error(getErrorMessageForRequest({ requestName: 'resendEmailVerification', response: err.response }))
    }
  }

  return {
    verification,
    clearVerification: () => setVerification(null),
    requestCode,
    requestLoading,
    requestError,
    setRequestError,
    showReauth,
    reauthPurpose: pendingReauth && pendingReauth.purpose,
    handleReauthenticate,
    cancelReauth: () => setShowReauth(false),
    submitCode,
    resend,
  }
}
